import React, { useMemo, useState } from "react";
import styled from "styled-components";
import {
  colors,
  CheckmarkIcon,
  EllipsisIcon,
  fontFamilies,
  XIcon,
} from "@commonsku/styles";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import {
  ClientColumn as BaseClientColumn,
  ProjectColumn as BaseProjectColumn,
  CreditMemoNumberColumn as BaseCreditMemoNumberColumn,
} from "../common";
import { formatDateFromString } from "../../../../utils";
import { useReportContext } from "../../report_context";
import DropdownMenu, { MenuTrigger } from "../../../DropdownMenu";
import ManuallyMarkAsExportedPopup from "../../../finance/ManuallyMarkAsExported";

export const InvoiceNumberColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Link
> = {
  header: <>Invoice&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_number", "invoice_form_number"],
  accessorKey: "invoice_number",
  canSort: true,
  orderByField: "invoice_form_number",
  width: 96,
  justify: "center",
  dataTransform: ({ job_number, invoice_form_number }) => ({
    href: `/project/${job_number}/invoice/${invoice_form_number}`,
    text: invoice_form_number,
  }),
};

export const CreditAmountColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Money
> = {
  header: <>Credit&nbsp;Amount</>,
  type: BASE_COLUMN_TYPES.Money,
  fields: ["total_total", "currency_id"],
  accessorKey: "total",
  canSort: true,
  orderByField: "total_total",
  width: 151,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ total_total, currency_id }) => ({
    amount: total_total,
    currency_id,
  }),
};

export const DateIssuedColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Date
> = {
  header: <>Date&nbsp;Issued</>,
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_issued"],
  accessorKey: "date_issued",
  canSort: true,
  orderByField: "date_issued",
  width: 151,
  dateOptions: { locales: "en-US", day: "numeric", month: "short", year: "numeric" },
};

export const ExportedDateColumn: ReportColumnConfigBase<
  typeof BASE_COLUMN_TYPES.Generic
> = {
  header: "Exported",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["credit_memo_id", "date_exported"],
  accessorKey: "date_exported",
  canSort: true,
  orderByField: "date_exported",
  width: 151,
  dataTransform: ({ date_exported }) => {
    if (!date_exported) {
      return {
        cell: <XIcon color={colors.neutrals["60"]} />,
      };
    }
    return {
      cell: (
        <>
          <CheckmarkIcon color={colors.green.main} />
          {formatDateFromString(date_exported, {
            locales: "en-US",
            day: "numeric",
            month: "short",
            year: "numeric",
            emptyValue: "",
          })}
        </>
      ),
    };
  },
};

const StyledDropdownMenu = styled(DropdownMenu)`
  & > .dropdown-menu-trigger {
    color: ${colors.primary1.main};
    border-radius: 5px;
    padding: 4px 4px 0 4px;
  }
  &.dropdown-menu-open > .dropdown-menu-trigger {
    border: 2px solid ${colors.primary1.main};
  }
  &&& > ul {
    border: 2px solid ${colors.primary1.main};
    border-radius: 5px;
    margin-top: 4px;
    top: -0.5rem;
    right: 3rem;
    min-width: 200px;

    & > li {
      font-family: ${fontFamilies.regular};
      color: ${colors.neutrals.bodyText};
    }
  }
`;

function CreditMemoActions({ credit_memo_id }: { credit_memo_id: string }) {
  const { fetchReport } = useReportContext();
  const [manuallyMarkAsExported, setManuallyMarkAsExported] = useState(false);
  const options = useMemo(
    () => [
      {
        value: "Manually Mark as Exported",
        hideOnClick: true,
        onClick: () => setManuallyMarkAsExported(true),
      },
    ],
    []
  );

  return (
    <>
      {manuallyMarkAsExported && (
        <ManuallyMarkAsExportedPopup
          documentType="credit_memo"
          id={credit_memo_id}
          onClose={(saved) => {
            setManuallyMarkAsExported(false);
            if (saved) {
              fetchReport();
            }
          }}
        />
      )}
      <StyledDropdownMenu options={options} align="right">
        <MenuTrigger>
          <EllipsisIcon altText="Actions" />
        </MenuTrigger>
      </StyledDropdownMenu>
    </>
  );
}

const ActionColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> = {
  header: "Actions",
  type: BASE_COLUMN_TYPES.Generic,
  fields: ["credit_memo_id"],
  accessorKey: "credit_memo_id",
  canSort: false,
  width: 120,
  justify: "center",
  capabilities: ["EXPORT-REPORT"],
  when: (identity) => "QBO" === (identity?.accounting_software ?? ""),
  dataTransform: ({ credit_memo_id }) => ({
    cell: <CreditMemoActions credit_memo_id={credit_memo_id} />,
  }),
};

const ClientColumn = { ...BaseClientColumn, width: 124 };
const ProjectColumn = { ...BaseProjectColumn, width: 301 };
const CreditMemoNumberColumn = { ...BaseCreditMemoNumberColumn, width: 138 };

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ClientColumn,
  ProjectColumn,
  InvoiceNumberColumn,
  CreditMemoNumberColumn,
  CreditAmountColumn,
  DateIssuedColumn,
  ExportedDateColumn,
  ActionColumn,
];
